import type { MouseEvent } from 'react';
import { useCallback, useRef } from 'react';
import { styled } from 'styled-components';

import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { closeDropdownFromElementWithin } from '#/components/Dropdown/closeDropdownFromElementWithin';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { LocationInput } from '#/components/LocationInput/LocationInput';
import { UncontrolledSearchQueryInputContainer } from '#/containers/UncontrolledSearchQueryInputContainer';
import { useEnvironment } from '#/contexts/UserEnvironmentContext';
import { useKeyedState } from '#/hooks/useKeyedState';
import { useI18n } from '#/i18n/hooks/useI18n';
import { UncontrolledListingTypeSelect } from '#/layouts/Layout/ListingTypeSelect/UncontrolledListingTypeSelect';
import { searchTypes } from '#/modules/search/constants/searchTypes';
import { searchGetJobsRoutePath } from '#/modules/search/jobs/routing/searchGetJobsRoutePath';
import { getPopularSearchesBySearchType } from '#/modules/search/mappings/getPopularSearchesBySearchType';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import type { SearchType } from '#/modules/search/types/SearchType';
import { colors, cssBreakpoints } from '#/theme/theme';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import { scrollToTop } from '#/utils/scroll';

const SearchInputsForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  margin: 0 auto;
  padding: 0;
  font-size: 18px;

  @media all and (min-width: ${cssBreakpoints.smUp}) {
    max-width: 400px;
  }

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    max-width: none;
  }
`;

type Props = {
  initialQuery: string;
  defaultSearchType: SearchType;
  onSearch: (data: {
    query: string;
    listingType: SearchType;
    triggeredByRemoteLocationChange: boolean;
  }) => void;
  // Location
  searchLocation: SearchLocation | undefined;
  onChangeSearchLocation: (
    searchLocation: SearchLocation | undefined,
  ) => boolean;
  // Login/Signup
  redirectParam: string;
  showLogin: boolean;
  clickLogin: (event: MouseEvent<HTMLAnchorElement>) => void;
  clickSignup: (event: MouseEvent<HTMLAnchorElement>) => void;
};

export function SearchHeroInputs({
  initialQuery,
  defaultSearchType,
  onSearch,
  searchLocation,
  onChangeSearchLocation,
  redirectParam,
  showLogin,
  clickLogin,
  clickSignup,
}: Props) {
  const { i18n, locale } = useI18n();
  const { environment } = useEnvironment();

  const [searchTypeForPopularSearches, setSearchTypeForPopularSearches] =
    useKeyedState(defaultSearchType, {
      key: defaultSearchType,
    });

  const popularSearches = getPopularSearchesBySearchType(
    i18n,
    searchTypeForPopularSearches,
  );

  const formRef = useRef<HTMLFormElement>(null);
  const submit = useCallback(
    (options: { triggeredByRemoteLocationChange: boolean }) => {
      const form = formRef.current;
      if (!form) return;

      const formData = new FormData(form);
      onSearch({
        query: formData.get('q') as string,
        listingType: formData.get('listing-type') as SearchType,
        triggeredByRemoteLocationChange:
          options.triggeredByRemoteLocationChange,
      });

      closeDropdownFromElementWithin(form);
    },
    [onSearch],
  );

  return (
    <SearchInputsForm
      ref={formRef}
      method="GET"
      action={searchGetJobsRoutePath({ locale })}
      onSubmit={(event) => {
        event.preventDefault();
        submit({ triggeredByRemoteLocationChange: false });
      }}
      data-qa-id="search-inputs"
    >
      <Box component="label" display="block" flex="1">
        <Box display="none">{i18n('Select listing type')}</Box>
        <UncontrolledListingTypeSelect
          name="listing-type"
          defaultValue={defaultSearchType}
          searchTypes={searchTypes}
          variant="outline-blue"
          onChange={(listingType) => {
            trackEvent('Changed Search Type', {
              filter_type: listingType,
              context: 'Hero',
            });
            setSearchTypeForPopularSearches(listingType);
          }}
        />
      </Box>

      <Box height="100%" width="100%" flex={[null, null, null, '2']}>
        <UncontrolledSearchQueryInputContainer
          name="q"
          placeholder={i18n('Search by keyword, skill, or interest')}
          initialQuery={initialQuery}
          presentation="light-border"
          id="search-query"
          qaId="search-input"
          popularSearches={popularSearches}
          searchType={searchTypeForPopularSearches}
        />
      </Box>

      <div style={{ flex: 1 }}>
        <LocationInput
          affixVariant="transparent"
          styleVariant="light-border"
          hasRemoteOptions
          showLocationIcon
          showClearButton
          placeholder={i18n('Everywhere')}
          locationText={searchLocation?.text}
          onChange={(newLocation) => {
            // Avoid submitting twice - the LocationInput has a bug that triggers the onChange twice
            if (!onChangeSearchLocation(newLocation)) return;

            // Submit when location is selected, but not when it's cleared
            if (newLocation) {
              submit({
                triggeredByRemoteLocationChange: Boolean(newLocation.isRemote),
              });
            }
          }}
          id="search-hero-location"
        />
      </div>

      <Box
        flex="0 0 auto"
        height="100%"
        display={['block', null, null, 'flex']}
        mt="16px"
      >
        <Button
          type="submit"
          variant="primary"
          icon={{ name: 'search' }}
          qaId="search-button"
          data-ready={Boolean(environment)}
          onClick={scrollToTop}
          size="large"
          fullWidth
        >
          {i18n('Search')}
        </Button>

        {redirectParam && showLogin && (
          <Box
            flex="1 1 auto"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            mt={[24, null, null, 0]}
          >
            <StyledNativeLinkWithRef
              href={`/login?${redirectParam}`}
              rel="nofollow"
              qaId="search-hero-login-link"
              onClick={clickLogin}
            >
              {i18n('Log In')}
            </StyledNativeLinkWithRef>

            <Box mx="8px" color={colors.elementGrey}>
              |
            </Box>

            <StyledNativeLinkWithRef
              href={`/signup?${redirectParam}`}
              rel="nofollow"
              qaId="search-hero-signup-link"
              onClick={clickSignup}
            >
              {i18n('Sign up')}
            </StyledNativeLinkWithRef>
          </Box>
        )}
      </Box>
    </SearchInputsForm>
  );
}
